import { IconButton } from '@material-ui/core'
import { Tooltip } from '@mui/material'
import upperFirst from 'lodash/upperFirst'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlineFileExcel } from 'react-icons/ai'
import { TableCellProps, Index, TableRowProps } from 'react-virtualized'

import api from 'src/api'
import { Fees, Fees_PolicyKindEnum, PenaltyScheme, PolicyAmount_StatusEnum } from 'src/api/gen/api'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import { CellLink } from 'src/components/Table/Cells'
import ExportableTable from 'src/components/Table/Templates/ExportableTable'
import { enumCellRenderer } from 'src/components/Table/utils'
import { IColumnProps } from 'src/components/Table/VirtualizedTable'

import { ProrationsColumn } from './ProrationsColumn'

export const ACCORDION_ROW_HEIGHT = 21

export const getProrationsLineCount = function (prorations: { [key: string]: number }): number {
  const count = Object.values(prorations).length
  if (count === 0) {
    return 2 // For the total
  } else {
    return count + 3 // +2 for the divider and total
  }
}

export const getRowHeightFunctionForProrations = function (prorations: { prorations?: { [key: string]: number } }[]) {
  return function (index: Index) {
    const prorationsLineCount = getProrationsLineCount(prorations[index.index]?.prorations ?? {})
    // +1 for the Total
    return ACCORDION_ROW_HEIGHT * prorationsLineCount
  }
}

interface IProps {
  className?: string
  data: Fees[]
  fees_scheme: PenaltyScheme
  loading?: boolean
  totalCount: number
}

const FeeSummaryTable = (props: IProps) => {
  const { t } = useTranslation()

  const columns: IColumnProps[] = [
    {
      label: upperFirst(t('titles.name.fee_rates')),
      width: 180,
      dataKey: 'policy_name',
      cellRenderer: (cellProps: TableCellProps) => {
        const feeRates = cellProps.rowData as Fees
        return (
          <CellLink url={`/fee_rate/${feeRates.policy_id}/details`} tooltip>
            {cellProps.cellData}
          </CellLink>
        )
      },
    },
    {
      label: upperFirst(t('titles.kind.fee_rates')),
      width: 180,
      dataKey: 'policy_kind',
      cellRenderer: (cellProps: TableCellProps) =>
        enumCellRenderer(cellProps, (kind: Fees_PolicyKindEnum) =>
          upperFirst(t(enumsTranslationKeys.PolicyKind[kind]))
        ),
    },
    {
      label: upperFirst(t('titles.penalty_scheme.status')),
      width: 180,
      dataKey: 'status',
      cellRenderer: (cellProps: TableCellProps) =>
        enumCellRenderer(cellProps, (status: PolicyAmount_StatusEnum) =>
          upperFirst(t(enumsTranslationKeys.PenaltySchemesStatus[status]))
        ),
    },
    {
      label: upperFirst(t('titles.amount')),
      width: 180,
      dataKey: 'amount',
      cellRenderer: (cellProps: TableCellProps) => {
        const fee = cellProps.rowData as Fees
        // prorations and amount are undefined only in the case of rowGetter returning the default empty object
        return <ProrationsColumn prorations={fee.prorations ?? {}} total_amount={fee.amount ?? 0} />
      },
    },
  ]

  const endRowRenderer = (rowRendererProps: TableRowProps) => {
    if ((rowRendererProps.rowData as Fees).policy_kind === Fees_PolicyKindEnum.trip_taxation) {
      return (
        <Tooltip title={<p style={{ fontSize: 13 }}>{t('common.export_line')}</p>} placement="bottom">
          <IconButton
            onClick={async () => {
              await api.offenses.tripTaxationLineFeesExport({
                provider_ids: [(rowRendererProps.rowData as Fees).provider_id],
                policy_ids: [(rowRendererProps.rowData as Fees).policy_id],
                trip_datetime_range_start: props.fees_scheme.start_date,
                trip_datetime_range_end: props.fees_scheme.end_date,
              })
            }}
          >
            <AiOutlineFileExcel style={{ fontSize: 20, cursor: 'pointer', color: '#192233' }} />
          </IconButton>
        </Tooltip>
      )
    }
    return <span style={{ paddingRight: 44, paddingTop: 44 }} />
  }

  return (
    <ExportableTable
      data={props.data}
      totalCount={props.totalCount}
      columns={columns}
      className={props.className}
      setIsOverDimension={function (_: boolean) {
        // nothing to see here
      }}
      rowHeight={getRowHeightFunctionForProrations(props.data)}
      endRowRenderer={endRowRenderer}
      endRowRendererWidth={44}
    />
  )
}

export default FeeSummaryTable
